import React, { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';

import { TextField, MenuItem, Button, Box, Autocomplete, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const AddEditSchool = ({
    providerIntakeDetails,
    accountResourceDetails,
    options,
    readOnly,
    getAndSetARDetails,
    setSchoolCurrentlyEditingId
}) => {
    const [errorMessage, setErrorMessage] = useState('');

    const theme = useTheme();

    const coachJobTitleId = '56d52962-1a56-ed11-bba3-00224809cdad';

    const validationSchema = Yup.object({
        school: Yup.object().nullable().required('School is required'),
        salaryType: Yup.string().nullable().required('Salary Type is required'),
        payRate: Yup.number().nullable().required('Pay Rate is required'),
        weeklyHours: Yup.number().nullable().required('Weekly Hours is required').integer('Weekly Hours must be a whole number'),
        jobTitleId: Yup.string().nullable().required('Job Title is required'),
        taxType: Yup.string()
            .nullable()
            .when('jobTitleId', {
                is: (val) => val === coachJobTitleId,
                then: (schema) => schema.required('Tax Type is required'),
                otherwise: (schema) => schema.notRequired()
            })
    });

    const formik = useFormik({
        initialValues: {
            school: accountResourceDetails?.school || null,
            salaryType: accountResourceDetails?.salaryType || '',
            payRate: accountResourceDetails?.rate || '',
            weeklyHours: accountResourceDetails?.weeklyHours || '',
            jobTitleId: accountResourceDetails?.jobTitle?.id || '',
            taxType: accountResourceDetails?.taxType || ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setErrorMessage('');

            const submitValues = {
                id: accountResourceDetails?.id || null,
                schoolId: values.school?.id,
                salaryType: values.salaryType,
                rate: values.payRate || null,
                weeklyHours: values.weeklyHours || null,
                jobTitleId: values.jobTitleId || null,
                taxType: values.taxType,
                ...providerIntakeDetails
            };

            try {
                await axios.post('/api/providerintake/saveaccountresource', submitValues);
                await getAndSetARDetails();
                setSchoolCurrentlyEditingId(null);
            } catch (error) {
                setErrorMessage(error);
            }
        }
    });

    const { values, setFieldValue, errors, touched, handleBlur, setFieldTouched, handleSubmit, isSubmitting, dirty } = formik;

    return (
        <FormikProvider value={formik}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                <Autocomplete
                    value={values.school || null}
                    onChange={(_, newValue) => setFieldValue('school', newValue || null)}
                    onBlur={() => setFieldTouched('school')}
                    options={options?.schools || []}
                    getOptionLabel={(option) => option?.name}
                    disabled={readOnly}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="School"
                            name="school"
                            error={touched.school && Boolean(errors.school)}
                            helperText={touched.school && errors.school}
                        />
                    )}
                />
                <TextField
                    name="salaryType"
                    value={values.salaryType || ''}
                    select
                    label="Salary Type"
                    onChange={(e) => setFieldValue('salaryType', e.target.value)}
                    onBlur={handleBlur}
                    error={touched.salaryType && Boolean(errors.salaryType)}
                    helperText={touched.salaryType && errors.salaryType}
                    disabled={readOnly}
                >
                    {options?.salaryTypes?.map((type, i) => (
                        <MenuItem key={i} value={type}>
                            {type}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    name="payRate"
                    value={values.payRate}
                    label="Pay Rate"
                    type="number"
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('payRate', e.target.value)}
                    error={touched.payRate && Boolean(errors.payRate)}
                    helperText={touched.payRate && errors.payRate}
                    disabled={readOnly}
                />
                <TextField
                    name="weeklyHours"
                    value={values.weeklyHours}
                    label="Weekly Hours"
                    type="number"
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('weeklyHours', e.target.value)}
                    error={touched.weeklyHours && Boolean(errors.weeklyHours)}
                    helperText={touched.weeklyHours && errors.weeklyHours}
                    disabled={readOnly}
                />
                <TextField
                    name="jobTitleId"
                    value={values.jobTitleId || ''}
                    select
                    label="Job Title"
                    onChange={(e) => setFieldValue('jobTitleId', e.target.value)}
                    onBlur={handleBlur}
                    error={touched.jobTitleId && Boolean(errors.jobTitleId)}
                    helperText={touched.jobTitleId && errors.jobTitleId}
                    disabled={readOnly}
                >
                    {options?.jobTitles?.map((title) => (
                        <MenuItem key={title.id} value={title.id}>
                            {title.name}
                        </MenuItem>
                    ))}
                </TextField>
                {values.jobTitleId === coachJobTitleId && (
                    <TextField
                        name="taxType"
                        value={values.taxType}
                        select
                        label="Tax Type"
                        onChange={(e) => setFieldValue('taxType', e.target.value)}
                        onBlur={handleBlur}
                        error={touched.taxType && Boolean(errors.taxType)}
                        helperText={touched.taxType && errors.taxType}
                        disabled={readOnly}
                    >
                        {options?.taxTypes?.map((type, i) => (
                            <MenuItem key={i} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {errorMessage && (
                    <Alert severity="error" onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}
                {!readOnly && (
                    <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting || !dirty}>
                        {isSubmitting && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: theme.palette.primary.main,
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px'
                                }}
                            />
                        )}
                        Save
                    </Button>
                )}
            </Box>
        </FormikProvider>
    );
};

export default AddEditSchool;
